(function() {
  'use strict';

  const MOBILE_BREAKPOINT = 749;

  function isMobile() {
    return window.innerWidth <= MOBILE_BREAKPOINT;
  }

  /**
   * Renderiza a media (image ou video) baseado no viewport atual.
   * Remove qualquer media anterior antes de inserir a nova.
   */
  function renderSlideMedia(slide) {
    const wrap = slide.querySelector('[data-bigmad-media-wrap]');
    if (!wrap) return;

    // Remove qualquer media anterior (mas mantém o overlay)
    const existing = wrap.querySelectorAll('.bigmad-slider-media');
    existing.forEach(el => el.remove());

    const mobile = isMobile();
    const isFirst = slide.dataset.isFirst === 'true';
    const alt = slide.dataset.alt || '';

    // Determina qual media usar
    const mediaType = mobile
      ? slide.dataset.mediaTypeMobile
      : slide.dataset.mediaTypeDesktop;

    const overlay = wrap.querySelector('.bigmad-slider-overlay');

    if (mediaType === 'video') {
      const videoStr = mobile ? slide.dataset.videoMobile : slide.dataset.videoDesktop;
      const poster = mobile ? slide.dataset.videoMobilePoster : slide.dataset.videoDesktopPoster;

      if (videoStr && videoStr.length > 0) {
        const video = createVideo(videoStr, poster, alt, isFirst);
        wrap.insertBefore(video, overlay);
        return;
      }

      // Fallback: se vídeo não existe, tenta imagem do mesmo viewport
      const fallbackImage = createImage(slide, mobile, isFirst);
      if (fallbackImage) wrap.insertBefore(fallbackImage, overlay);
    } else {
      const img = createImage(slide, mobile, isFirst);
      if (img) wrap.insertBefore(img, overlay);
    }
  }

  function createImage(slide, mobile, isFirst) {
    const src = mobile ? slide.dataset.imageMobile : slide.dataset.imageDesktop;
    const srcset = mobile ? slide.dataset.imageMobileSrcset : slide.dataset.imageDesktopSrcset;
    const width = mobile ? slide.dataset.imageMobileW : slide.dataset.imageDesktopW;
    const height = mobile ? slide.dataset.imageMobileH : slide.dataset.imageDesktopH;
    const alt = slide.dataset.alt || '';

    if (!src) return null;

    const img = document.createElement('img');
    img.className = 'bigmad-slider-media';
    img.src = src;
    if (srcset) img.srcset = srcset;
    img.sizes = '100vw';
    img.alt = alt;
    img.width = parseInt(width, 10) || 1920;
    img.height = parseInt(height, 10) || 1080;

    if (isFirst) {
      img.loading = 'eager';
      img.setAttribute('fetchpriority', 'high');
    } else {
      img.loading = 'lazy';
      img.setAttribute('fetchpriority', 'low');
    }

    // Fade-in quando carregar
    if (img.complete) {
      img.classList.add('is-loaded');
    } else {
      img.addEventListener('load', () => img.classList.add('is-loaded'), { once: true });
    }

    return img;
  }

  function createVideo(videoStr, poster, alt, isFirst) {
    const video = document.createElement('video');
    video.className = 'bigmad-slider-media';
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.setAttribute('aria-label', alt);

    if (poster) video.poster = poster;
    video.preload = isFirst ? 'auto' : 'metadata';
    if (isFirst) video.setAttribute('fetchpriority', 'high');

    // Parse sources do data-attribute (formato: "url|type;url|type;")
    const sources = videoStr.split(';').filter(s => s.trim().length > 0);
    sources.forEach(sourceStr => {
      const [url, type] = sourceStr.split('|');
      if (url) {
        const source = document.createElement('source');
        source.src = url;
        if (type) source.type = type;
        video.appendChild(source);
      }
    });

    // Fade-in quando começar a tocar
    video.addEventListener('loadeddata', () => {
      video.classList.add('is-loaded');
    }, { once: true });

    // Tenta dar play manualmente (alguns browsers bloqueiam)
    video.addEventListener('canplay', () => {
      video.play().catch(() => {});
    }, { once: true });

    return video;
  }

  function initSlider(section) {
    const track = section.querySelector('[data-bigmad-slider-track]');
    if (!track) return;

    const dots = section.querySelectorAll('[data-bigmad-slider-dot]');
    const prevBtn = section.querySelector('[data-bigmad-slider-prev]');
    const nextBtn = section.querySelector('[data-bigmad-slider-next]');
    const slides = section.querySelectorAll('[data-bigmad-slide]');

    // Renderiza media de cada slide
    slides.forEach(renderSlideMedia);

    // Re-renderiza ao mudar de tamanho (com debounce)
    let lastIsMobile = isMobile();
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const currentIsMobile = isMobile();
        if (currentIsMobile !== lastIsMobile) {
          lastIsMobile = currentIsMobile;
          slides.forEach(renderSlideMedia);
        }
      }, 250);
    });

    // Se só tem 1 slide, não inicia navegação nem autoplay
    if (dots.length <= 1) return;

    // ============ AUTOPLAY ============
    const autoplay = section.dataset.autoplay === 'true';
    const autoplaySpeed = parseInt(section.dataset.autoplaySpeed, 10) * 1000 || 6000;
    let autoplayTimer = null;
    let isPaused = false;

    function startAutoplay() {
      if (!autoplay || isPaused) return;
      stopAutoplay();
      autoplayTimer = setInterval(() => {
        if (!isPaused) goToNext();
      }, autoplaySpeed);
    }

    function stopAutoplay() {
      if (autoplayTimer) {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    function pauseAutoplayTemporarily(duration = 10000) {
      isPaused = true;
      stopAutoplay();
      setTimeout(() => {
        isPaused = false;
        startAutoplay();
      }, duration);
    }

    // Pausa quando mouse entra, retoma quando sai (só desktop)
    if (!isMobile()) {
      section.addEventListener('mouseenter', () => {
        isPaused = true;
        stopAutoplay();
      });

      section.addEventListener('mouseleave', () => {
        isPaused = false;
        startAutoplay();
      });
    }

    // Pausa quando aba não está visível
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopAutoplay();
      } else if (autoplay && !isPaused) {
        startAutoplay();
      }
    });

    // ============ NAVEGAÇÃO ============
    function goToIndex(index) {
      track.scrollTo({
        left: track.offsetWidth * index,
        behavior: 'smooth'
      });
    }

    function goToNext() {
      const currentIndex = Math.round(track.scrollLeft / track.offsetWidth);
      const nextIndex = (currentIndex + 1) % dots.length;
      goToIndex(nextIndex);
    }

    function goToPrev() {
      const currentIndex = Math.round(track.scrollLeft / track.offsetWidth);
      const prevIndex = currentIndex === 0 ? dots.length - 1 : currentIndex - 1;
      goToIndex(prevIndex);
    }

    function updateActiveDot() {
      const index = Math.round(track.scrollLeft / track.offsetWidth);
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    // Eventos
    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        goToNext();
        pauseAutoplayTemporarily();
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        goToPrev();
        pauseAutoplayTemporarily();
      });
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index, 10) || 0;
        goToIndex(index);
        pauseAutoplayTemporarily();
      });
    });

    // Touch — pausa autoplay durante interação
    track.addEventListener('touchstart', () => {
      isPaused = true;
      stopAutoplay();
    }, { passive: true });

    track.addEventListener('touchend', () => {
      setTimeout(() => {
        isPaused = false;
        startAutoplay();
      }, 3000);
    }, { passive: true });

    // Atualiza dot ao rolar
    let scrollTimer;
    track.addEventListener('scroll', () => {
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(updateActiveDot, 50);
    }, { passive: true });

    // ============ INICIA AUTOPLAY ============
    if (autoplay) {
      // Espera 1.5s antes do primeiro autoplay (deixa usuário ver o primeiro)
      setTimeout(startAutoplay, 1500);
    }
  }

  function init() {
    const sections = document.querySelectorAll('[data-bigmad-slider]');
    sections.forEach(initSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init quando o Shopify recarrega a seção no editor
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-bigmad-slider]');
    if (section) initSlider(section);
  });
})();
